import { environment } from 'environment';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { cleanBool } from 'shared';
import { useNavContext } from '../layouts/nav/context';

const DynUserAcls = dynamic(() => import('./user-acls').then(mod => mod.UserACLs), { ssr: false });

export function Meta({
  title,
  description = 'Beds for Kids is a nonprofit furniture bank in Charlotte, NC, focused on providing beds and essential furniture to families in need.',
  imgUrl = 'https://content.bedsforkids.org/static/logo-grt.png',
  noDefaultFooter,
  noFooter,
  auth,
  noAuthorized,
  admin,
  referralViewer,
  referralPartner,
  referralManager,
  inventoryManager,
  staff,
  accountant,
  adminBgColor,
  blueTopArch,
}: {
  title?: string;
  description?: string;
  imgUrl?: string;
  noDefaultFooter?: boolean;
  noFooter?: boolean;
  auth?: boolean;
  noAuthorized?: boolean;
  admin?: boolean;
  referralViewer?: boolean;
  referralPartner?: boolean;
  referralManager?: boolean;
  inventoryManager?: boolean;
  staff?: boolean;
  accountant?: boolean;
  adminBgColor?: string;
  blueTopArch?: boolean;
}) {
  const router = useRouter();
  const navData = useNavContext();

  useEffect(() => {
    if (noDefaultFooter && navData.defaultFooter) navData.setNavContext('defaultFooter', false);
    if (!noDefaultFooter && !navData.defaultFooter) navData.setNavContext('defaultFooter', true);
  }, [noDefaultFooter]);

  useEffect(() => {
    if (noFooter && navData.footer) navData.setNavContext('footer', false);
    if (!noFooter && !navData.footer) navData.setNavContext('footer', true);
  }, [noFooter]);

  useEffect(() => {
    if (!blueTopArch && navData.blueTopArch) navData.setNavContext('blueTopArch', false);
    if (blueTopArch && !navData.blueTopArch) navData.setNavContext('blueTopArch', true);
  }, [blueTopArch]);

  useEffect(() => {
    if (adminBgColor && navData.adminBgColor) navData.setNavContext('adminBgColor', adminBgColor);
    if (!adminBgColor && navData.adminBgColor !== 'bg-gray-100') navData.setNavContext('adminBgColor', 'bg-gray-100');
  }, [adminBgColor]);

  return (
    <>
      <Head>
        <title>{getTitle()}</title>
        <meta name="description" content={description} />

        {/* OpenGraph Tags */}
        <meta property="og:url" content={`https://${environment.domains.frontend}/`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={getTitle()} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imgUrl} />

        {/* Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={environment.domains.frontend} />
        <meta
          property="twitter:url"
          content={`https://${environment.domains.frontend}/${cleanBool(router.pathname)}`}
        />
        <meta name="twitter:title" content={getTitle()} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imgUrl} />
      </Head>

      {/* User ACLs */}
      <DynUserAcls
        noUnauthorized={auth}
        noAuthorized={noAuthorized}
        admin={admin}
        referralViewer={referralViewer}
        referralPartner={referralPartner}
        referralManager={referralManager}
        inventoryManager={inventoryManager}
        staff={staff}
        accountant={accountant}
      />
    </>
  );

  function getTitle() {
    return title ? (admin ? `${title} | Admin | BFK` : `${title} | BFK`) : `Beds for Kids`;
  }
}
